import { Grid } from '@mui/material';
import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { SearchContext } from '../Context/SearchContext/SearchContext';
import { GarmentObject, TimestampsObject } from '../types/reducers/filterReducer';
import GarmentResult from './GarmentResult/GarmentResultComponent';
import SearchResultsInput from './SearchResultsInput';

function BaseSearchResults({
  externalFilteredGarmentObjects,
}: {
  externalFilteredGarmentObjects: GarmentObject[];
}) {
  // USE STATE
  const [page, setPage] = useState(0);
  const [localSearchResults, setLocalSearchResults] = useState(externalFilteredGarmentObjects);

  const numberOfResultsPerScrollLoad = 8;

  useEffect(() => {
    if (localSearchResults.length === externalFilteredGarmentObjects.length) return;
    setLocalSearchResults(externalFilteredGarmentObjects);
  }, [externalFilteredGarmentObjects]);

  useEffect(() => {
    function handleScroll() {
      const { innerHeight, pageYOffset } = window;
      const heightOfPage = document.body.clientHeight;
      const heightFromTopPlus50Pixels = pageYOffset + innerHeight;
      const weAreWithin400PixelsOfTheBottom = heightOfPage - heightFromTopPlus50Pixels < 400;
      if (
        weAreWithin400PixelsOfTheBottom &&
        localSearchResults.length > page * numberOfResultsPerScrollLoad
      ) {
        setPage((prev) => prev + 1);
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [localSearchResults, externalFilteredGarmentObjects]);

  return (
    <>
      <SearchResultsInput
        externalFilteredGarmentObjects={externalFilteredGarmentObjects}
        localSearchResults={localSearchResults}
        setLocalSearchResults={setLocalSearchResults}
      />
      <Grid
        sx={{ flexGrow: 1, width: '98%', margin: 'auto' }}
        container
        direction='row'
        justifyContent='center'
        alignItems='flex-start'
        alignContent='center'
        spacing={1}
      >
        {localSearchResults
          .slice(0, page * numberOfResultsPerScrollLoad + numberOfResultsPerScrollLoad)
          .map((thisObj) => (
            <Grid key={thisObj.teemeasuresaverages_id} item xs={12} sm={4} md={5} lg={4}>
              <GarmentResult garmentObject={thisObj} />
            </Grid>
          ))}
      </Grid>
    </>
  );
}

// If true then don't re-render
function comparisonFunction(
  prevProps: Readonly<
    PropsWithChildren<{
      externalFilteredGarmentObjects: GarmentObject[];
      timestamps: TimestampsObject;
    }>
  >,
  nextProps: Readonly<
    PropsWithChildren<{
      externalFilteredGarmentObjects: GarmentObject[];
      timestamps: TimestampsObject;
    }>
  >
) {
  // return false in order to re-render. We only want to re-render when the externalFilterePosts have changed
  const timestampsHaveNotChanged =
    prevProps.timestamps.externalFilteredGarmentObjects ===
    nextProps.timestamps.externalFilteredGarmentObjects;
  // There's also a case where we don't update the timestamp on load because the results are too recent to warrant another call to back end. In this case, check the lengths.
  const resultsHaveNotChanged =
    prevProps.externalFilteredGarmentObjects.length ===
    nextProps.externalFilteredGarmentObjects.length;
  return timestampsHaveNotChanged && resultsHaveNotChanged;
}

const MemoisedSearchResults = React.memo(BaseSearchResults, comparisonFunction);

function SearchResults() {
  const { filterState } = useContext(SearchContext);
  return (
    <MemoisedSearchResults
      externalFilteredGarmentObjects={filterState.externalFilteredGarmentObjects}
      timestamps={filterState.timestamps}
    />
  );
}

export default SearchResults;
